import { Component, computed, signal, inject, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { MatTooltipModule } from '@angular/material/tooltip';
import { provideNativeDateAdapter } from '@angular/material/core';
import { CounselorAvailability } from './counselor-availability/counselor-availability';
import {
  DayAvailability,
  BlockedPeriod,
  MeetingPreferences,
  OnlinePlatform,
} from '../../../../../models/meeting-preferences';
import {
  MAX_INSTRUCTIONS_LENGTH,
  MAX_MEETINGS_PER_DAY,
  blockedPeriodsError,
  instructionsError,
  meetingDurationError,
  meetingsPerDayError,
  weeklyHoursError,
} from '../../../../../helpers/meeting-preferences-helpers';
import { UserMeetingPreferencesService } from '../../../../../services/user-meeting-preferences.service';
import { UserService } from '../../../../../services/user.service';

@Component({
  selector: 'app-counselor-meeting-preferences',
  imports: [
    DatePipe,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatTimepickerModule,
    MatTooltipModule,
    CounselorAvailability,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './counselor-meeting-preferences.html',
  styleUrl: './counselor-meeting-preferences.css',
})
export class CounselorMeetingPreferences implements OnInit {
  private prefsService = inject(UserMeetingPreferencesService);
  private userService = inject(UserService);
  private currentUserId?: number;

  /** The durations a counselor can offer, in minutes. */
  readonly durationOptions = [15, 30, 45, 60];

  /** Exposed for the meetings-per-day input's `max` attribute. */
  readonly maxMeetingsPerDay = MAX_MEETINGS_PER_DAY;

  /** Exposed for the instructions textarea's `maxlength` and character counter. */
  readonly maxInstructionsLength = MAX_INSTRUCTIONS_LENGTH;

  /** Lower bound for the Block Off Time pickers, so the panel opens around business hours instead of midnight. */
  readonly earliestTime = MeetingPreferences.at(8, 0);

  /** Today at midnight — blocking off a date that has already passed does
   *  nothing, and there's deliberately no upper bound here: unlike the booking
   *  calendars, a counselor may need to block leave further out than students
   *  can book. */
  readonly earliestDate = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  })();

  // ── Draft state, one signal per card slice ─────────────────────────────
  readonly weeklyAvailability = signal<DayAvailability[]>(MeetingPreferences.defaultDays());
  readonly allowedDurations = signal<number[]>([15, 30, 45]);
  readonly meetingsPerDay = signal<number | null>(null);
  readonly inPersonLocation = signal<string | null>(null);
  readonly onlinePlatform = signal<OnlinePlatform | null>(null);
  readonly instructions = signal<string | null>(null);

  readonly editing = signal(false);

  /** Restored previously saved values on cancel. */
  private snapshot: MeetingPreferences | null = null;

  // ── Block Off Time ──────────────────────────────────────────────────────
  /** Only meaningful for a block added this session, before the next save/reload assigns a real id. */
  private nextBlockId = 1;
  readonly blockedPeriods = signal<BlockedPeriod[]>([]);

  readonly showBlockForm = signal(false);
  readonly blockType = signal<'single' | 'range'>('single');
  readonly blockDate = signal<Date | null>(null);
  readonly blockRangeStart = signal<Date | null>(null);
  readonly blockRangeEnd = signal<Date | null>(null);
  readonly blockAllDay = signal(true);
  readonly blockStartTime = signal<Date | null>(null);
  readonly blockEndTime = signal<Date | null>(null);
  readonly blockReason = signal('');

  /**
   * Whether the in-progress add-block form has everything it needs to save.
   * A single-day block needs a date, plus a start/end time when it isn't
   * all-day; a range block just needs both endpoints (ranges are always
   * all-day, so no time fields apply).
   */
  readonly blockFormValid = computed(() => {
    if (this.blockType() === 'single') {
      if (!this.blockDate()) return false;
      return this.blockAllDay() || (this.blockStartTime() !== null && this.blockEndTime() !== null);
    }
    return this.blockRangeStart() !== null && this.blockRangeEnd() !== null;
  });

  // ── Validation roll-up ─────────────────────────────────────────────────
  /** Per-card message, keyed by the card's title for the Save tooltip. */
  readonly errors = computed<Record<string, string | null>>(() => ({
    'Weekly Hours': weeklyHoursError(this.weeklyAvailability()),
    'Meeting Duration': meetingDurationError(this.allowedDurations()),
    'Meetings per Day': meetingsPerDayError(this.meetingsPerDay()),
    'Additional Information': instructionsError(this.instructions()),
    'Block Off Time': blockedPeriodsError(this.blockedPeriods()),
  }));

  readonly hasErrors = computed(() => Object.values(this.errors()).some((error) => error !== null));

  /**
   * Returns the reason the save action is failing.
   */
  readonly saveBlockedReason = computed(() => {
    const failing = Object.entries(this.errors()).find(([, error]) => error !== null);
    return failing ? `${failing[0]} — ${failing[1]}` : null;
  });

  /** Loads the current user, then their saved preferences, into the draft signals. */
  ngOnInit(): void {
    this.userService.getCurrentUser().subscribe({
      next: (user) => {
        this.currentUserId = user.id;
        this.prefsService.getUserMeetingPreferences(user.id).subscribe({
          next: (prefs) => this.applyPreferences(MeetingPreferences.digest(prefs)),
          // No saved preferences yet — the default signal values already stand in.
          error: () => {},
        });
      },
    });
  }

  /** Add or remove a duration, keeping the selection sorted ascending. */
  toggleDuration(minutes: number): void {
    this.allowedDurations.update((durations) =>
      durations.includes(minutes)
        ? durations.filter((d) => d !== minutes)
        : [...durations, minutes].sort((a, b) => a - b),
    );
  }

  // ── Block Off Time ──────────────────────────────────────────────────────
  /** Whether a block's start and end fall on the same calendar day, for display and to gate the time-window fields. */
  isSingleDay(block: BlockedPeriod): boolean {
    return block.startDate.toDateString() === block.endDate.toDateString();
  }

  /**
   * Appends a new block from the in-progress add-block form and resets it.
   * No-op if the form isn't valid yet. Range blocks always save as all-day —
   * the form only collects a time window when blockType is 'single'.
   */
  addBlock(): void {
    if (!this.blockFormValid()) return;

    const single = this.blockType() === 'single';
    this.blockedPeriods.update((blocks) => [
      ...blocks,
      {
        id: this.nextBlockId++,
        startDate: single ? this.blockDate()! : this.blockRangeStart()!,
        endDate: single ? this.blockDate()! : this.blockRangeEnd()!,
        allDay: single ? this.blockAllDay() : true,
        startTime: single && !this.blockAllDay() ? this.blockStartTime() : null,
        endTime: single && !this.blockAllDay() ? this.blockEndTime() : null,
        reason: this.blockReason().trim(),
      },
    ]);
    this.cancelBlockForm();
  }

  /** Removes one blocked period by id. */
  removeBlock(id: number): void {
    this.blockedPeriods.update((blocks) => blocks.filter((block) => block.id !== id));
  }

  /** Resets the add-block form fields to their defaults and hides it. */
  cancelBlockForm(): void {
    this.blockType.set('single');
    this.blockDate.set(null);
    this.blockRangeStart.set(null);
    this.blockRangeEnd.set(null);
    this.blockAllDay.set(true);
    this.blockStartTime.set(null);
    this.blockEndTime.set(null);
    this.blockReason.set('');
    this.showBlockForm.set(false);
  }

  // ── Mode transitions ───────────────────────────────────────────────────
  edit(): void {
    this.snapshot = this.currentDraft();
    this.editing.set(true);
  }

  cancel(): void {
    if (this.snapshot) {
      this.weeklyAvailability.set(this.snapshot.weeklyAvailability);
      this.allowedDurations.set(this.snapshot.allowedDurations);
      this.meetingsPerDay.set(this.snapshot.meetingsPerDay);
      this.inPersonLocation.set(this.snapshot.inPersonLocation);
      this.onlinePlatform.set(this.snapshot.onlinePlatform);
      this.instructions.set(this.snapshot.instructions);
      this.blockedPeriods.set(this.snapshot.blockedAvailabilityIntervals);
      this.snapshot = null;
    }
    this.cancelBlockForm();
    this.editing.set(false);
  }

  /** Persists the draft via the service; a failed save leaves editing open so the counselor can retry. */
  save(): void {
    if (this.hasErrors()) return;
    if (!this.currentUserId) return;

    const payload = this.currentDraft().toPayload();
    this.prefsService.updateUserMeetingPreferences(this.currentUserId, payload).subscribe({
      next: () => {
        this.snapshot = null;
        this.editing.set(false);
      },
      error: () => {
        // optional: surface a save-failed message; for now we just stay in edit mode
      },
    });
  }

  /** Snapshot-safe copy of the current draft; `toPayload()` turns it into the request body. */
  private currentDraft(): MeetingPreferences {
    const draft = new MeetingPreferences();
    draft.weeklyAvailability = MeetingPreferences.cloneDays(this.weeklyAvailability());
    draft.allowedDurations = [...this.allowedDurations()];
    draft.meetingsPerDay = this.meetingsPerDay();
    draft.inPersonLocation = this.inPersonLocation();
    draft.onlinePlatform = this.onlinePlatform();
    draft.instructions = this.instructions();
    draft.blockedAvailabilityIntervals = MeetingPreferences.cloneBlocks(this.blockedPeriods());
    return draft;
  }

  private applyPreferences(prefs: MeetingPreferences): void {
    this.weeklyAvailability.set(prefs.weeklyAvailability);
    this.allowedDurations.set(prefs.allowedDurations);
    this.meetingsPerDay.set(prefs.meetingsPerDay);
    this.inPersonLocation.set(prefs.inPersonLocation);
    this.onlinePlatform.set(prefs.onlinePlatform);
    this.instructions.set(prefs.instructions);
    this.blockedPeriods.set(prefs.blockedAvailabilityIntervals);
    // Keep locally-added blocks from colliding with real ids once the server assigns its own.
    this.nextBlockId =
      prefs.blockedAvailabilityIntervals.reduce((max, block) => Math.max(max, block.id), 0) + 1;
  }
}
